import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { FieldValue, FieldValues } from "../Schema";

/**
 * The kinds of client a source can be read through.
 *
 * The value of each member is the key its definition lives under in a {@link Source}, so a
 * client reads its own definition by its own kind and never has to know about the others.
 */
export enum SchemaClientKinds {
  GRAPHQL = "graphql",
  DYNAMODB = "dynamodb",
}

/** The DynamoDB operations a source can be read with. */
export enum DynamoDbOperations {
  QUERY = "query",
  SCAN = "scan",
  GET = "get",
}

/**
 * The tables the schema's sources read from, named logically.
 *
 * A physical table name carries its environment and region, so it cannot be declared here.
 * {@link SchemaClient} implementations are handed the mapping from these to the real names.
 */
export enum DynamoDbTables {
  SITES = "sites",
  BUILDINGS = "buildings",
  FLOORS = "floors",
  ROOMS = "rooms",
  SOPS = "sops",
  TAGS = "tags",
  LAB_AREAS = "labAreas",
  REASONS = "reasons",
  LINKED_INSTANCES = "linkedInstances",
  USERS = "users",
  GROUPS = "groups",
  ENTRIES = "entries",
  CATEGORY_MAPPINGS = "categoryMappings",
}

/**
 * One DynamoDB expression: the text, the names it escapes and the values it binds.
 *
 * Written out by hand, exactly as the GraphQL half of a source is. Nothing here is derived
 * from the GraphQL document — the two are independent definitions of the same read.
 */
export type DynamoDbExpression = {
  expression: string;
  names: Record<string, string>;
  values: Record<string, FieldValue>;
};

/**
 * A DynamoDB read, parameterised by the same variables as its GraphQL counterpart.
 *
 * The expressions are functions of the variables for the same reason a GraphQL document
 * takes variables: the shape is fixed and declared once, and only the bound values change
 * per call.
 */
export type DynamoDbRequest<V = FieldValues> = {
  operation: DynamoDbOperations;
  table: DynamoDbTables;
  /** Secondary index to read through. Omitted for a read against the table's own keys. */
  index?: string;
  /** The whole primary key. `GET` only. */
  key?: (variables: V) => Record<string, FieldValue>;
  /** The key condition. `QUERY` only. */
  keyCondition?: (variables: V) => DynamoDbExpression;
  /** Applied after the key condition, to non-key attributes. `QUERY` and `SCAN`. */
  filter?: (variables: V) => DynamoDbExpression | undefined;
  /** Sort-key order. Defaults to ascending, as AppSync does. */
  ascending?: boolean;
  /** Items per page. The client pages until the table is exhausted regardless. */
  limit?: number;
};

/**
 * Keeps only the rows still in use.
 *
 * Every master-data table carries `isActive`, and the GraphQL half of nearly every source
 * passes `filter: { isActive: { eq: true } }`. Declaring it once keeps the two halves from
 * drifting on the one condition they all share.
 *
 * @returns The filter expression.
 */
export const activeFilter = (): DynamoDbExpression => ({
  expression: "#isActive = :isActive",
  names: { "#isActive": "isActive" },
  values: { ":isActive": true },
});

/**
 * A source the schema reads master data from, defined once per client kind.
 *
 * Both definitions describe the same read and return the same shape, so a getter names the
 * source and the client picks the definition it can execute — see {@link SchemaClient}.
 *
 * A bare document is accepted as well, and means "GraphQL only". Plenty of reads have no
 * reason to exist on the other side, and a client that cannot serve one says so rather
 * than answering emptily.
 */
export type Source<T, V = FieldValues> =
  | TypedDocumentNode<T, V>
  | {
      [SchemaClientKinds.GRAPHQL]: TypedDocumentNode<T, V>;
      [SchemaClientKinds.DYNAMODB]?: DynamoDbRequest<V>;
    };

/**
 * Reads the GraphQL document out of a source.
 *
 * @param source - The source.
 * @returns The document, or `undefined` when the source does not define one.
 */
export const getGraphqlDocument = <T, V>(
  source: Source<T, V>
): TypedDocumentNode<T, V> | undefined => {
  if (SchemaClientKinds.GRAPHQL in source) {
    return source[SchemaClientKinds.GRAPHQL];
  }
  // A bare document — `kind: "Document"`, no per-client keys.
  return source as TypedDocumentNode<T, V>;
};

/**
 * Reads the DynamoDB request out of a source.
 *
 * @param source - The source.
 * @returns The request, or `undefined` when the source does not define one.
 */
export const getDynamoDbRequest = <T, V>(
  source: Source<T, V>
): DynamoDbRequest<V> | undefined => {
  if (SchemaClientKinds.DYNAMODB in source) {
    return source[SchemaClientKinds.DYNAMODB];
  }
  return undefined;
};
